import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Table,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, useEffect, useRef } from 'react';

import { cx } from '@/lib/cx';

import { Linha, type MetaColuna, Situacao } from './tabela-partes';

export type { MetaColuna } from './tabela-partes';

interface TabelaDadosProps<T> {
  rotulo: string;
  colunas: ColumnDef<T>[];
  dados: readonly T[];
  idLinha: (linha: T) => string;
  carregando?: boolean;
  /** Mostrado quando não há linhas (e não está carregando). */
  vazio: ReactNode;
  aoAbrir?: (linha: T, elemento: HTMLElement) => void;
  /** Ações que aparecem ao passar o mouse/focar a linha. */
  acoesLinha?: (linha: T) => ReactNode;
  /** Chamado perto do fim da lista — carregar a próxima página. */
  aoChegarAoFim?: () => void;
  selecionadaId?: string | null;
  className?: string;
}

const ALTURA_LINHA = 44;

/**
 * Tabela densa e virtualizada (milhares de notas sem travar). Cabeçalho fixo, linha inteira
 * clicável e acessível por teclado (Enter abre), ações reveladas no hover.
 */
export function TabelaDados<T>(props: TabelaDadosProps<T>) {
  const { rotulo, colunas, dados, idLinha, carregando = false, vazio, className } = props;
  const rolagem = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table devolve funções não memoizáveis; o componente não depende de memo.
  const tabela = useReactTable({
    data: dados as T[],
    columns: colunas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (linha) => idLinha(linha),
  });
  const linhas = tabela.getRowModel().rows;
  const virtual = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => rolagem.current,
    estimateSize: () => ALTURA_LINHA,
    // Medida inicial: a primeira pintura já mostra linhas, antes do ResizeObserver medir.
    initialRect: { width: 1024, height: 640 },
    overscan: 12,
  });
  const itens = virtual.getVirtualItems();
  useFimDaLista(itens.at(-1)?.index, linhas.length, props.aoChegarAoFim);

  const nColunas = colunas.length + (props.acoesLinha ? 1 : 0);
  const espacos = espacadores(itens, virtual.getTotalSize());

  return (
    <div
      ref={rolagem}
      className={cx(
        'relative min-h-0 overflow-auto rounded-cartao border border-borda bg-superficie',
        className,
      )}
    >
      <table
        aria-label={rotulo}
        aria-busy={carregando}
        className="w-full border-separate border-spacing-0 text-base"
      >
        <Cabecalho tabela={tabela} comAcoes={Boolean(props.acoesLinha)} />
        <tbody>
          <Situacao
            carregando={carregando}
            vazia={linhas.length === 0}
            colunas={nColunas}
            vazio={vazio}
          />
          {espacos.topo > 0 && <tr aria-hidden style={{ height: espacos.topo }} />}
          {itens.map((item) => {
            const linha = linhas[item.index];
            return linha ? <Linha key={linha.id} linha={linha} props={props} /> : null;
          })}
          {espacos.base > 0 && <tr aria-hidden style={{ height: espacos.base }} />}
        </tbody>
      </table>
    </div>
  );
}

/** Altura dos espaçadores acima e abaixo das linhas renderizadas (virtualização). */
function espacadores(itens: readonly { start: number; end: number }[], total: number) {
  const primeiro = itens[0];
  const ultimo = itens.at(-1);
  if (!primeiro || !ultimo) return { topo: 0, base: 0 };
  return { topo: primeiro.start, base: total - ultimo.end };
}

function Cabecalho<T>({ tabela, comAcoes }: { tabela: Table<T>; comAcoes: boolean }) {
  return (
    <thead className="sticky top-0 z-10 bg-superficie">
      {tabela.getHeaderGroups().map((grupo) => (
        <tr key={grupo.id}>
          {grupo.headers.map((cabecalho) => {
            const meta = cabecalho.column.columnDef.meta as MetaColuna | undefined;
            return (
              <th
                key={cabecalho.id}
                scope="col"
                style={meta?.largura ? { width: meta.largura } : undefined}
                className={cx(
                  'border-b border-borda px-3 py-2.5 text-left text-xs font-medium text-texto-suave',
                  meta?.alinhar === 'direita' && 'text-right',
                )}
              >
                {flexRender(cabecalho.column.columnDef.header, cabecalho.getContext())}
              </th>
            );
          })}
          {comAcoes && (
            <th scope="col" className="w-px border-b border-borda">
              <span className="sr-only">Ações</span>
            </th>
          )}
        </tr>
      ))}
    </thead>
  );
}

/** Avisa uma vez quando a última linha visível chega perto do fim dos dados. */
function useFimDaLista(ultimoVisivel: number | undefined, total: number, aoChegar?: () => void) {
  useEffect(() => {
    if (aoChegar && ultimoVisivel !== undefined && total > 0 && ultimoVisivel >= total - 5)
      aoChegar();
  }, [ultimoVisivel, total, aoChegar]);
}
